import { useSearchParams } from 'react-router';

import { SignedOutCard } from '../components/SignedOutCard';

/**
 * The post-logout interstitial (`/logged-out`).
 *
 * It exists to break the silent re-login loop: the api destroys the session and
 * 302s here (see `AuthController.logout`). Landing on `/auth/login` instead
 * would chain into oauth.yandex.ru, where the still-live Yandex session is
 * approved without a prompt and the callback immediately mints a fresh cookie —
 * the user is back inside and logout looks broken.
 *
 * It is also where a FAILED sign-in lands: the api redirects here with
 * `?login_failed=1` whenever an OAuth hop errors (Yandex error answer, state
 * mismatch, api hiccup), so the failure is visible instead of a silent bounce.
 *
 * Deliberately rendered OUTSIDE `AppLayout`: the shell probes `/auth/me` and
 * the data queries would 401 into the signed-out state, either of which would
 * bounce straight back into the OAuth flow. This page is a dead end whose only
 * exit is an explicit click on "Sign in" — which is a full-page navigation to
 * the api's `/auth/login`, so the Yandex round trip gets a real top-level
 * document.
 */
export function LoggedOutPage() {
  const [params] = useSearchParams();
  const loginFailed = params.get('login_failed') === '1';

  return (
    <SignedOutCard
      title="You are signed out"
      hint="This session is closed on this device. The Yandex account on this browser is not."
      warning={
        loginFailed
          ? 'The last sign-in attempt did not complete. Wait a moment, then click Sign in again — if it keeps failing, ask the administrator to check the api logs.'
          : undefined
      }
    />
  );
}
